'use client';

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, TrendingUp, ShoppingCart, ArrowUpCircle, Users, BarChart3, Calendar, CreditCard, Globe } from 'lucide-react';

import { MetricCard } from './MetricCard';
import { ChartContainer } from './ChartContainer';
import { useAggregatedMetrics } from '../hooks/useAggregatedMetrics';
import { RevenueCompositionChart, type RevenueCompositionViewMode } from './RevenueCompositionChart';
import { RevenueByProcessorChart, type RevenueByProcessorViewMode } from './RevenueByProcessorChart';
import { TransactionErrorsChart, type TransactionErrorsViewMode } from './TransactionErrorsChart';
import { RefundsByTypeChart, type RefundsByTypeViewMode } from './RefundsByTypeChart';
import { CustomerGeographicChart, type CustomerGeographicViewMode } from './CustomerGeographicChart';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useStraightSale } from '../hooks/metrics/useStraightSale';
import { useRebillRevenue } from '../hooks/metrics/useRebillRevenue';
import { useUpsell } from '../hooks/metrics/useUpsell';
import { useRefunds } from '../hooks/metrics/useRefunds';
import { formatMoney } from "@/lib/money";
import { EmptyState } from './EmptyState';
import { Skeleton } from '@/components/ui/skeleton';

interface RevenueMetricsV4Props {
    data: {
        straightSaleRevenueByDate?: Map<string, { revenue: number; saleCount: number }>;
        rebillRevenueByDate?: Map<string, { amount: number; transactionCount: number }>;
        upsellRevenueByDate?: Map<string, { revenue: number; upsellCount: number }>;
        refundsByDate?: Map<string, { amount: number; refundCount: number }>;
        averageLTVData?: { averageLTV: number; customerCount: number };
        revenueByProcessor?: { processorName: string; amount: number; count: number }[];
        transactionErrors?: { errorCode: string; count: number }[];
        refundsByType?: Map<string, {
            straightSale: { amount: number; count: number };
            rebill: { amount: number; count: number };
            upsell: { amount: number; count: number };
            other: { amount: number; count: number };
        }>;
        revenueByProcessorTimeSeries?: { data: Record<string, { [processorName: string]: { amount: number; count: number } }>; processors: string[] };
        transactionErrorsTimeSeries?: { data: Record<string, { [errorCode: string]: number }>; errorCodes: string[] };
        customerGeographicByCardCountry?: { countryCode: string; countryName: string; transactionCount: number; amount: number }[];
        customerGeographicByIpCountry?: { countryCode: string; countryName: string; transactionCount: number; amount: number }[];
    } | null | undefined;
    granularity: 'daily' | 'weekly' | 'monthly';
    currency: string;
    loadingStates: {
        rebillRevenue: boolean;
        refunds: boolean;
        straightSale: boolean;
        upsellRevenue: boolean;
        averageLTV: boolean;
        activeSubscriptions: boolean;
        subscriptionsGained: boolean;
        subscriptionsCancelled: boolean;
        approvalRates: boolean;
        subscriptionsByStatus: boolean;
        subscriptionsByStatusTimeSeries: boolean;
        revenueByProcessor: boolean;
        transactionErrors: boolean;
        refundsByType: boolean;
        revenueByProcessorTimeSeries: boolean;
        transactionErrorsTimeSeries: boolean;
        customerGeographicByCardCountry: boolean;
        customerGeographicByIpCountry: boolean;
    };
    error: any;
}

interface MetricSummaryProps {
    title: string;
    value: number;
    currency: string;
    icon: React.ComponentType<{ className?: string }>;
    volume?: number;
    volumeLabel?: string;
    emptyStateMessage?: string;
    isLoading?: boolean;
}

function MetricSummary({ title, value, currency, icon: Icon, volume, volumeLabel = 'transactions', emptyStateMessage, isLoading }: MetricSummaryProps) {
    const isEmpty = value === 0 && (volume === undefined || volume === 0);

    return (
        <div className="flex items-center justify-between p-4 bg-card border rounded-lg shadow-sm">
            <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                    <p className="text-sm font-medium text-muted-foreground">{title}</p>
                    {isLoading ? (
                        <div className="space-y-2 mt-2">
                            <Skeleton className="h-7 w-64" />
                            <Skeleton className="h-3 w-56" />
                        </div>
                    ) : isEmpty && emptyStateMessage ? (
                        <EmptyState message={emptyStateMessage} variant="inline" />
                    ) : (
                        <>
                            <p className="text-lg font-semibold text-foreground">{formatMoney(value, currency)}</p>
                            {volume && volume > 0 && (
                                <p className="text-xs text-muted-foreground">{volume.toLocaleString()} {volumeLabel}</p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function ViewModeToggle({ viewMode, onToggle, timeSeriesLabel = 'Time series', aggregatedLabel = 'Aggregated' }: { viewMode: 'timeSeries' | 'aggregated'; onToggle: () => void; timeSeriesLabel?: string; aggregatedLabel?: string }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <Button variant="ghost" size="sm" onClick={onToggle} className="h-7 w-7 p-0">
                    {viewMode === 'timeSeries' ? (
                        <BarChart3 className="h-4 w-4" />
                    ) : (
                        <Calendar className="h-4 w-4" />
                    )}
                </Button>
            </TooltipTrigger>
            <TooltipContent>
                <p>Switch to {viewMode === 'timeSeries' ? aggregatedLabel : timeSeriesLabel}</p>
            </TooltipContent>
        </Tooltip>
    );
}

export function RevenueMetricsV4({ data, granularity, currency, loadingStates, error }: RevenueMetricsV4Props) {
    const { t } = useTranslation('analytics');
    const [refundsByTypeViewMode, setRefundsByTypeViewMode] = useState<RefundsByTypeViewMode>('timeSeries');
    const [revenueCompositionViewMode, setRevenueCompositionViewMode] = useState<RevenueCompositionViewMode>('timeSeries');
    const [revenueByProcessorViewMode, setRevenueByProcessorViewMode] = useState<RevenueByProcessorViewMode>('timeSeries');
    const [transactionErrorsViewMode, setTransactionErrorsViewMode] = useState<TransactionErrorsViewMode>('timeSeries');
    const [customerGeographicViewMode, setCustomerGeographicViewMode] = useState<CustomerGeographicViewMode>('ipCountry');

    const straightSaleData = useStraightSale(data?.straightSaleRevenueByDate, granularity, currency);
    const rebillData = useRebillRevenue(data?.rebillRevenueByDate, granularity, currency);
    const upsellData = useUpsell(data?.upsellRevenueByDate, granularity, currency);
    const refundData = useRefunds(data?.refundsByDate, granularity, currency);

    const straightSaleMetrics = useAggregatedMetrics(straightSaleData);
    const rebillMetrics = useAggregatedMetrics(rebillData);
    const upsellMetrics = useAggregatedMetrics(upsellData);
    const refundMetrics = useAggregatedMetrics(refundData);

    const totalGrossRevenue = straightSaleMetrics.total + rebillMetrics.total + upsellMetrics.total;
    const totalRevenue = totalGrossRevenue - refundMetrics.total;
    const totalVolume = straightSaleMetrics.volume + rebillMetrics.volume + upsellMetrics.volume;

    const createTotalRevenueData = () => {
        if (!straightSaleData.length && !rebillData.length && !upsellData.length) return [] as any[];
        const allDates = new Set<string>();
        [straightSaleData, rebillData, upsellData, refundData].forEach((d) => { d.forEach((item) => allDates.add(item.code)); });
        return Array.from(allDates).sort().map((date) => {
            const straightSale = straightSaleData.find((item) => item.code === date);
            const rebill = rebillData.find((item) => item.code === date);
            const upsell = upsellData.find((item) => item.code === date);
            const refund = refundData.find((item) => item.code === date);
            const totalValue = (straightSale?.value || 0) + (rebill?.value || 0) + (upsell?.value || 0) - (refund?.value || 0);
            const totalVolume = (straightSale?.volume || 0) + (rebill?.volume || 0) + (upsell?.volume || 0);
            return { code: date, value: totalValue, volume: totalVolume, currency };
        });
    };

    const totalRevenueData = createTotalRevenueData();
    const averageLTVValue = data?.averageLTVData?.averageLTV ?? 0;
    const averageLTVCustomerCount = data?.averageLTVData?.customerCount ?? 0;

    if (error) {
        return (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                <MetricCard title={t('revenue.errorTitle')} description={t('revenue.errorDescription')} className="col-span-full">
                    <div className="flex items-center justify-center h-full">
                        <p className="text-destructive">{t('revenue.errorMessage')}</p>
                    </div>
                </MetricCard>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <MetricSummary title={t('revenue.totalRevenue')} value={totalRevenue} currency={currency} icon={DollarSign} volume={totalVolume} emptyStateMessage={t('revenue.noRevenue')} isLoading={loadingStates.straightSale || loadingStates.rebillRevenue || loadingStates.upsellRevenue || loadingStates.refunds} />
                <MetricSummary title={t('revenue.averageLTV')} value={averageLTVValue} currency={currency} icon={Users} volume={averageLTVCustomerCount} volumeLabel={t('revenue.customers')} emptyStateMessage={t('revenue.noCustomerData')} isLoading={loadingStates.averageLTV} />
                <MetricSummary title={t('revenue.straightSaleRevenue')} value={straightSaleMetrics.total} currency={currency} icon={ShoppingCart} volume={straightSaleMetrics.volume} emptyStateMessage={t('revenue.noStraightSales')} isLoading={loadingStates.straightSale} />
                <MetricSummary title={t('revenue.upsellRevenue')} value={upsellMetrics.total} currency={currency} icon={ArrowUpCircle} volume={upsellMetrics.volume} emptyStateMessage={t('revenue.noUpsells')} isLoading={loadingStates.upsellRevenue} />
                <MetricSummary title={t('revenue.rebillRevenue')} value={rebillMetrics.total} currency={currency} icon={TrendingUp} volume={rebillMetrics.volume} emptyStateMessage={t('revenue.noRebills')} isLoading={loadingStates.rebillRevenue} />
                <MetricSummary title={t('revenue.totalRefunds')} value={refundMetrics.total} currency={currency} icon={DollarSign} volume={refundMetrics.volume} emptyStateMessage={t('revenue.noRefunds')} isLoading={loadingStates.refunds} />
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                <MetricCard
                    title={t('revenue.revenueComposition')}
                    description={t('revenue.revenueCompositionDescription')}
                    isLoading={loadingStates.straightSale || loadingStates.rebillRevenue || loadingStates.upsellRevenue}
                    headerAction={
                        <ViewModeToggle
                            viewMode={revenueCompositionViewMode}
                            onToggle={() => setRevenueCompositionViewMode(prev => prev === 'timeSeries' ? 'aggregated' : 'timeSeries')}
                        />
                    }
                >
                    <RevenueCompositionChart straightSaleData={straightSaleData} rebillData={rebillData} upsellData={upsellData} currency={currency} viewMode={revenueCompositionViewMode} />
                </MetricCard>

                <MetricCard title={t('revenue.totalRevenue')} description={t('revenue.totalRevenueDescription')} isLoading={loadingStates.straightSale || loadingStates.rebillRevenue || loadingStates.upsellRevenue || loadingStates.refunds}>
                    <ChartContainer data={totalRevenueData} chartType="line" currency={currency} emptyStateMessage={t('revenue.noRevenueRecorded')} />
                </MetricCard>

                <MetricCard title={t('revenue.straightSaleRevenue')} description={t('revenue.straightSaleDescription')} isLoading={loadingStates.straightSale}>
                    <ChartContainer data={straightSaleData} chartType="bar" currency={currency} emptyStateMessage={t('revenue.noStraightSalesRange')} />
                </MetricCard>

                <MetricCard title={t('revenue.rebillRevenue')} description={t('revenue.rebillDescription')} isLoading={loadingStates.rebillRevenue}>
                    <ChartContainer data={rebillData} chartType="line" currency={currency} emptyStateMessage={t('revenue.noRebillsRecorded')} />
                </MetricCard>

                <MetricCard title={t('revenue.upsellRevenue')} description={t('revenue.upsellDescription')} isLoading={loadingStates.upsellRevenue}>
                    <ChartContainer data={upsellData} chartType="bar" currency={currency} emptyStateMessage={t('revenue.noUpsellsRange')} />
                </MetricCard>

                <MetricCard
                    title={t('revenue.refunds')}
                    description={t('revenue.refundsDescription')}
                    isLoading={loadingStates.refundsByType}
                    headerAction={
                        <ViewModeToggle
                            viewMode={refundsByTypeViewMode}
                            onToggle={() => setRefundsByTypeViewMode(prev => prev === 'timeSeries' ? 'aggregated' : 'timeSeries')}
                        />
                    }
                >
                    <RefundsByTypeChart data={data?.refundsByType} currency={currency} viewMode={refundsByTypeViewMode} granularity={granularity} />
                </MetricCard>

                <MetricCard
                    title={t('revenue.revenueByProcessor')}
                    description={t('revenue.revenueByProcessorDescription')}
                    isLoading={revenueByProcessorViewMode === 'timeSeries' ? loadingStates.revenueByProcessorTimeSeries : loadingStates.revenueByProcessor}
                    dynamicHeight={revenueByProcessorViewMode === 'aggregated'}
                    headerAction={
                        <ViewModeToggle
                            viewMode={revenueByProcessorViewMode}
                            onToggle={() => setRevenueByProcessorViewMode(prev => prev === 'timeSeries' ? 'aggregated' : 'timeSeries')}
                            timeSeriesLabel={t('revenue.timeSeries')}
                        />
                    }
                >
                    <RevenueByProcessorChart
                        data={data?.revenueByProcessor}
                        timeSeriesData={data?.revenueByProcessorTimeSeries}
                        currency={currency}
                        viewMode={revenueByProcessorViewMode}
                        granularity={granularity}
                    />
                </MetricCard>

                <MetricCard
                    title={t('revenue.transactionErrors')}
                    description={t('revenue.transactionErrorsDescription')}
                    isLoading={transactionErrorsViewMode === 'timeSeries' ? loadingStates.transactionErrorsTimeSeries : loadingStates.transactionErrors}
                    dynamicHeight={transactionErrorsViewMode === 'aggregated'}
                    headerAction={
                        <ViewModeToggle
                            viewMode={transactionErrorsViewMode}
                            onToggle={() => setTransactionErrorsViewMode(prev => prev === 'timeSeries' ? 'aggregated' : 'timeSeries')}
                            timeSeriesLabel={t('revenue.timeSeries')}
                        />
                    }
                >
                    <TransactionErrorsChart
                        data={data?.transactionErrors}
                        timeSeriesData={data?.transactionErrorsTimeSeries}
                        viewMode={transactionErrorsViewMode}
                        granularity={granularity}
                    />
                </MetricCard>

                <MetricCard
                    title={t('revenue.customerGeographic')}
                    description={customerGeographicViewMode === 'ipCountry' ? t('revenue.geoDescriptionIp') : t('revenue.geoDescriptionCard')}
                    isLoading={customerGeographicViewMode === 'cardCountry' ? loadingStates.customerGeographicByCardCountry : loadingStates.customerGeographicByIpCountry}
                    dynamicHeight
                    headerAction={
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setCustomerGeographicViewMode(prev => prev === 'cardCountry' ? 'ipCountry' : 'cardCountry')}
                                    className="h-7 px-2 gap-1"
                                >
                                    {customerGeographicViewMode === 'cardCountry' ? (
                                        <>
                                            <CreditCard className="h-4 w-4" />
                                            <span className="text-xs">{t('revenue.card')}</span>
                                        </>
                                    ) : (
                                        <>
                                            <Globe className="h-4 w-4" />
                                            <span className="text-xs">{t('revenue.ip')}</span>
                                        </>
                                    )}
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{customerGeographicViewMode === 'cardCountry' ? t('revenue.switchToIpCountry') : t('revenue.switchToCardCountry')}</p>
                            </TooltipContent>
                        </Tooltip>
                    }
                >
                    <CustomerGeographicChart
                        cardCountryData={data?.customerGeographicByCardCountry}
                        ipCountryData={data?.customerGeographicByIpCountry}
                        currency={currency}
                        viewMode={customerGeographicViewMode}
                    />
                </MetricCard>
            </div>
        </div>
    );
}


